// ResetPassword.js
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import PasswordResetConfirm from '../../graphql/mutations/PasswordResetConfirm';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleReset = async () => {
        if (password.length < 6) {
            return setError(t('passwordAtLeast6', 'Password must be at least 6 characters.'));
        }
        if (password !== passwordConfirmation) {
            return setError(t('passwordsDontMatch', "Passwords don't match."));
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await PasswordResetConfirm.commit({
                password,
                passwordConfirmation,
                resetPasswordToken: token,
            });

            const { passwordResetConfirm } = response;
            if (passwordResetConfirm.success) {
                setSuccess(t('passwordResetSuccess', 'Password successfully reset. You can now sign in.'));
                setTimeout(() => navigate('/sign-in'), 2000);
            } else {
                setError(passwordResetConfirm.error || t('invalidOrExpiredToken', 'Invalid or expired token.'));
            }
        } catch (err) {
            console.error('Password reset error:', err);
            setError(t('somethingWentWrong', 'Something went wrong.'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box
                sx={{
                    maxWidth: '400px',
                    margin: '50px auto',
                    padding: 4,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'primary.main',
                    }}
                >
                    {t('resetPassword', 'Reset Password')}
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <TextField
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="password"
                    autoComplete="off"
                    label={t('newPassword', 'New Password')}
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="password"
                    autoComplete="off"
                    label={t('confirmPassword', 'Confirm Password')}
                    variant="outlined"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    sx={{ mb: 3 }}
                />
                <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    disabled={isLoading || !token}
                    onClick={handleReset}
                    sx={{ fontSize: '1rem', padding: '12px 0', textTransform: 'none' }}
                >
                    {isLoading && <CircularProgress size={20} color="inherit" sx={{ marginRight: '15px' }} />}
                    {t('setNewPassword', 'Set New Password')}
                </Button>
            </Box>
        </Container>
    );
};

export default ResetPassword;
